import React from 'react';
import { motion } from 'framer-motion';
import { FiThumbsUp, FiInstagram, FiLinkedin, FiMessageSquare, FiImage, FiMessageCircle, FiVideo, FiAtSign, FiPlay } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export const SocialMediaPage: React.FC = () => {
  useDocumentTitle(
    'Results-Driven Social Media Marketing | Expert Team for Online Growth',
    'Grow your brand with simple, effective social media marketing designed for real engagement. Our social media marketing services help you reach the right audience with clear communication.'
  );

  const services = [
    {
      id: 'facebook-marketing',
      title: 'Facebook Marketing',
      icon: FiThumbsUp,
      description: 'Engage with billions of users through strategic Facebook marketing campaigns.',
      features: [
        'Facebook Page Management',
        'Content Creation & Scheduling',
        'Facebook Ads Management',
        'Community Engagement',
        'Analytics & Reporting',
        'Facebook Shop Setup'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹12,000/month', features: ['15 Posts/Month', 'Basic Ad Campaign', 'Page Management', 'Monthly Reports', 'Email Support'] },
        pro: { name: 'Professional', price: '₹28,000/month', features: ['30 Posts/Month', 'Advanced Ad Campaigns', 'Community Management', 'Weekly Reports', 'Priority Support'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Posts', 'Full Ad Management', 'Dedicated Manager', 'Daily Reports', '24/7 Support'] }
      }
    },
    {
      id: 'instagram-marketing',
      title: 'Instagram Marketing',
      icon: FiInstagram,
      description: 'Grow your brand with stunning visuals and engaging Instagram content.',
      features: [
        'Instagram Feed Management',
        'Stories & Reels Creation',
        'Instagram Ads',
        'Influencer Collaborations',
        'Hashtag Strategy',
        'Shopping Setup'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹15,000/month', features: ['20 Posts + Stories', 'Basic Ad Campaign', 'Hashtag Research', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹32,000/month', features: ['40 Posts + Reels', 'Advanced Ads', 'Influencer Outreach', 'Weekly Analytics', 'Priority Support'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Content', 'Full Ad Suite', 'Dedicated Team', 'Real-time Analytics', '24/7 Support'] }
      }
    },
    {
      id: 'linkedin-marketing',
      title: 'LinkedIn Marketing',
      icon: FiLinkedin,
      description: 'Build professional connections and establish thought leadership on LinkedIn.',
      features: [
        'LinkedIn Page Management',
        'Professional Content Creation',
        'LinkedIn Ads',
        'Lead Generation',
        'Employee Advocacy',
        'Performance Analytics'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹10,000/month', features: ['12 Posts/Month', 'Basic Networking', 'Page Optimization', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹25,000/month', features: ['25 Posts/Month', 'LinkedIn Ads', 'Lead Gen Campaigns', 'Weekly Reports', 'Priority Support'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Posts', 'Full Ad Management', 'Employee Programs', 'Dedicated Manager', '24/7 Support'] }
      }
    },
    {
      id: 'twitter-marketing',
      title: 'Twitter Marketing',
      icon: FiMessageSquare,
      description: 'Join real-time conversations and build your brand presence on Twitter.',
      features: [
        'Tweet Scheduling & Management',
        'Twitter Ads Campaigns',
        'Community Engagement',
        'Twitter Analytics',
        'Influencer Partnerships',
        'Crisis Management'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹8,000/month', features: ['15 Tweets/Month', 'Basic Analytics', 'Community Management', 'Email Support'] },
        pro: { name: 'Professional', price: '₹18,000/month', features: ['50 Tweets/Month', 'Twitter Ads', 'Advanced Analytics', 'Priority Support', 'Monthly Strategy'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Tweets', 'Full Ad Management', 'Dedicated Manager', '24/7 Support', 'Crisis Management'] }
      }
    },
    {
      id: 'pinterest-marketing',
      title: 'Pinterest Marketing',
      icon: FiImage,
      description: 'Drive traffic and sales with visually compelling Pinterest marketing strategies.',
      features: [
        'Pin Design & Creation',
        'Pinterest SEO Optimization',
        'Board Management',
        'Pinterest Ads',
        'Rich Pins Setup',
        'Analytics & Reporting'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹10,000/month', features: ['20 Pins/Month', 'Basic SEO', '3 Boards', 'Monthly Reports', 'Email Support'] },
        pro: { name: 'Professional', price: '₹22,000/month', features: ['60 Pins/Month', 'Advanced SEO', '10 Boards', 'Pinterest Ads', 'Weekly Reports', 'Priority Support'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Pins', 'Full SEO Suite', 'Unlimited Boards', 'Advanced Ads', 'Dedicated Manager', '24/7 Support'] }
      }
    },
    {
      id: 'whatsapp-marketing',
      title: 'WhatsApp Marketing',
      icon: FiMessageCircle,
      description: 'Connect directly with customers through WhatsApp Business and automation.',
      features: [
        'WhatsApp Business Setup',
        'Broadcast Campaigns',
        'Chatbot Integration',
        'Customer Support Automation',
        'WhatsApp Catalog',
        'Analytics Dashboard'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹12,000/month', features: ['Up to 1000 Contacts', 'Basic Automation', 'Broadcast Messages', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹28,000/month', features: ['Up to 5000 Contacts', 'Advanced Chatbot', 'Catalog Management', 'CRM Integration', 'Weekly Reports'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Contacts', 'Custom Chatbot', 'API Integration', 'Dedicated Manager', 'Real-time Analytics'] }
      }
    },
    {
      id: 'tiktok-marketing',
      title: 'TikTok Marketing',
      icon: FiVideo,
      description: 'Create viral content and reach younger audiences with TikTok marketing.',
      features: [
        'TikTok Content Creation',
        'Video Editing & Production',
        'TikTok Ads Management',
        'Influencer Collaborations',
        'Trend Analysis',
        'Performance Analytics'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹15,000/month', features: ['8 Videos/Month', 'Basic Editing', 'Hashtag Strategy', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹35,000/month', features: ['20 Videos/Month', 'Professional Editing', 'TikTok Ads', 'Influencer Outreach', 'Weekly Analytics'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Videos', 'Premium Production', 'Full Ad Management', 'Dedicated Team', 'Real-time Reporting'] }
      }
    },
    {
      id: 'threads-marketing',
      title: 'Threads Marketing',
      icon: FiAtSign,
      description: 'Build authentic connections on Threads with engaging text-based content.',
      features: [
        'Threads Content Strategy',
        'Daily Post Management',
        'Community Building',
        'Cross-Platform Integration',
        'Engagement Analytics',
        'Influencer Partnerships'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹8,000/month', features: ['15 Posts/Month', 'Basic Strategy', 'Community Management', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹18,000/month', features: ['45 Posts/Month', 'Advanced Strategy', 'Active Engagement', 'Instagram Integration', 'Weekly Reports'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Posts', 'Full Strategy Suite', 'Dedicated Manager', 'Multi-Platform', '24/7 Support'] }
      }
    },
    {
      id: 'youtube-marketing',
      title: 'YouTube Marketing',
      icon: FiPlay,
      description: 'Grow your channel and audience with strategic YouTube marketing and optimization.',
      features: [
        'Video Content Strategy',
        'SEO & Optimization',
        'Thumbnail Design',
        'Channel Management',
        'YouTube Ads',
        'Analytics & Growth'
      ],
      pricing: {
        basic: { name: 'Starter', price: '₹20,000/month', features: ['4 Videos/Month', 'Basic SEO', 'Thumbnail Design', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹45,000/month', features: ['8 Videos/Month', 'Advanced SEO', 'YouTube Ads', 'Channel Optimization', 'Weekly Analytics'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Videos', 'Full Production', 'Complete Management', 'Dedicated Team', 'Real-time Analytics'] }
      }
    }
  ];

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-6 relative z-10"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Social Media Marketing
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-8">
              Build your brand, engage audiences, and drive growth across all social platforms
            </p>
          </div>
        </motion.div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent"></div>
      </section>

      {/* Services Sections */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="mb-32 last:mb-0"
            >
              {/* Service Header */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white mb-6"
                     style={{
                       boxShadow: '0 20px 40px rgba(236, 72, 153, 0.3), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.1)'
                     }}>
                  <service.icon className="w-10 h-10" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">{service.title}</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">{service.description}</p>
              </div>

              {/* Features Grid */}
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="grid md:grid-cols-3 gap-6 mb-16"
              >
                {service.features.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className="bg-white p-6 rounded-xl border-2 border-gray-100 hover:border-pink-300 transition-all duration-300"
                    whileHover={{ 
                      y: -5,
                      boxShadow: '0 10px 30px rgba(236, 72, 153, 0.2)'
                    }}
                    style={{
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-pink-600 rounded-full mr-3"></div>
                      <p className="text-gray-700 font-medium">{feature}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-3 gap-8">
                {/* Basic */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-pink-400 transition-all duration-300"
                  style={{
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1), inset -2px -2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.pricing.basic.name}</h3>
                  <div className="text-4xl font-bold text-pink-600 mb-6">{service.pricing.basic.price}</div>
                  <ul className="space-y-4 mb-8">
                    {service.pricing.basic.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-lg transition-colors duration-300"
                  >
                    Get Started
                  </Link>
                </motion.div>

                {/* Pro */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-600 rounded-2xl p-8 text-white relative transform scale-105 z-10"
                  style={{
                    boxShadow: '0 20px 50px rgba(236, 72, 153, 0.4), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-4 py-1 rounded-bl-lg rounded-tr-xl font-bold text-sm">
                    POPULAR
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{service.pricing.pro.name}</h3>
                  <div className="text-4xl font-bold mb-6">{service.pricing.pro.price}</div>
                  <ul className="space-y-4 mb-8">
                    {service.pricing.pro.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-300 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-white text-pink-600 hover:bg-gray-100 font-semibold py-3 rounded-lg transition-colors duration-300"
                  >
                    Get Started
                  </Link>
                </motion.div>

                {/* Enterprise */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-purple-400 transition-all duration-300"
                  style={{
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1), inset -2px -2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.pricing.enterprise.name}</h3>
                  <div className="text-4xl font-bold text-purple-600 mb-6">{service.pricing.enterprise.price}</div>
                  <ul className="space-y-4 mb-8">
                    {service.pricing.enterprise.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-colors duration-300"
                  >
                    Contact Sales
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Dominate Social Media?
            </h2>
            <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto">
              Let's create a winning social media strategy that grows your brand and engages your audience
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-pink-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
              style={{
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
              }}
            >
              Get Free Strategy Session
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};